// Períodos del reporte financiero y su comparación con el período anterior.
// Las fechas salen de los componentes locales de `hoy`, nunca de toISOString().

export type PresetPeriodo = 'mes_actual' | 'ultimos_3_meses' | 'ultimos_6_meses' | 'ultimos_12_meses' | 'anio_actual'

/** Mes calendario cerrado, en formato 'YYYY-MM'. */
export type MesPeriodo = `${number}-${string}`

/** Lo que se elige en el filtro: un preset o un mes concreto. */
export type SeleccionPeriodo = PresetPeriodo | MesPeriodo

/** Rango de fechas inclusivo, en formato 'YYYY-MM-DD'. */
export interface Periodo {
  desde: string
  hasta: string
}

export interface PeriodoComparado {
  actual: Periodo
  anterior: Periodo
}

export const PRESET_DEFAULT: PresetPeriodo = 'ultimos_12_meses'

export const PRESETS_PERIODO: { value: PresetPeriodo; label: string }[] = [
  { value: 'mes_actual', label: 'Este mes' },
  { value: 'ultimos_3_meses', label: 'Último trimestre' },
  { value: 'ultimos_6_meses', label: 'Último semestre' },
  { value: 'ultimos_12_meses', label: 'Último año' },
  { value: 'anio_actual', label: 'Año en curso' },
]

const MESES_PRESET: Record<Exclude<PresetPeriodo, 'anio_actual'>, number> = {
  mes_actual: 1,
  ultimos_3_meses: 3,
  ultimos_6_meses: 6,
  ultimos_12_meses: 12,
}

const esMes = (seleccion: SeleccionPeriodo): seleccion is MesPeriodo => /^\d{4}-\d{2}$/.test(seleccion)

/**
 * Meses cerrados que se pueden elegir, del más reciente al más viejo: desde el mes anterior a `hoy`
 * hasta el mes de `primeraFecha`. El mes en curso no entra porque es el preset «Este mes».
 * `primeraFecha` es la fecha 'YYYY-MM-DD' del primer movimiento. Sin fecha, la lista es vacía.
 */
export const listarMesesCerrados = (primeraFecha: string | null, hoy: Date): { value: MesPeriodo; label: string }[] => {
  if (!primeraFecha) return []
  const primerMes = primeraFecha.slice(0, 7)
  const meses: { value: MesPeriodo; label: string }[] = []
  for (let mes = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1); ; mes.setMonth(mes.getMonth() - 1)) {
    const value = aISO(mes).slice(0, 7) as MesPeriodo
    if (value < primerMes) break
    const label = mes.toLocaleString('es-AR', { month: 'long', year: 'numeric' })
    meses.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) })
  }
  return meses
}

const aISO = (fecha: Date): string =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`

/** Mueve una fecha `meses` hacia atrás. Si el día no existe en el mes destino, usa el último día de ese mes. */
const restarMeses = (fecha: Date, meses: number): Date => {
  const ultimoDiaDestino = new Date(fecha.getFullYear(), fecha.getMonth() - meses + 1, 0).getDate()
  return new Date(fecha.getFullYear(), fecha.getMonth() - meses, Math.min(fecha.getDate(), ultimoDiaDestino))
}

/** Ventana de `meses` meses que termina en `hasta` y arranca el día 1, contando el mes de `hasta`. */
const ventanaHasta = (hasta: Date, meses: number): { desde: Date; hasta: Date } => ({
  desde: new Date(hasta.getFullYear(), hasta.getMonth() - (meses - 1), 1),
  hasta,
})

const aPeriodo = ({ desde, hasta }: { desde: Date; hasta: Date }): Periodo => ({
  desde: aISO(desde),
  hasta: aISO(hasta),
})

/**
 * Calcula el período de la selección y el período anterior con el que se compara.
 * Un mes elegido es el mes completo y se compara con el mes completo anterior.
 * En un preset, el período anterior es el mismo tramo desplazado un preset hacia atrás: del 1 al 22/09
 * se compara con el 1 al 22/08.
 */
export const calcularPeriodo = (seleccion: SeleccionPeriodo, hoy: Date): PeriodoComparado => {
  const dia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())

  if (esMes(seleccion)) {
    const [anio, mes] = seleccion.split('-').map(Number)
    return {
      actual: aPeriodo(ventanaHasta(new Date(anio, mes, 0), 1)),
      anterior: aPeriodo(ventanaHasta(new Date(anio, mes - 1, 0), 1)),
    }
  }

  if (seleccion === 'anio_actual') {
    const haceUnAnio = restarMeses(dia, 12)
    return {
      actual: { desde: `${dia.getFullYear()}-01-01`, hasta: aISO(dia) },
      anterior: { desde: `${haceUnAnio.getFullYear()}-01-01`, hasta: aISO(haceUnAnio) },
    }
  }

  const meses = MESES_PRESET[seleccion]
  return {
    actual: aPeriodo(ventanaHasta(dia, meses)),
    anterior: aPeriodo(ventanaHasta(restarMeses(dia, meses), meses)),
  }
}

/** Variación porcentual contra el período anterior. Devuelve null si el anterior es 0, porque no hay base. */
export const calcularVariacion = (actual: number, anterior: number): number | null => {
  if (anterior === 0) return null
  return ((actual - anterior) / Math.abs(anterior)) * 100
}

/**
 * Variación de un flujo contra el período anterior. Devuelve null si los datos empiezan después del
 * inicio del período anterior, porque la base está incompleta, o si el anterior es 0.
 * `inicioDatos` es la fecha 'YYYY-MM-DD' del primer registro del flujo.
 */
export const calcularVariacionFlujo = (
  actual: number,
  anterior: number,
  inicioDatos: string | null,
  periodoAnterior: Periodo,
): number | null => {
  if (!inicioDatos || inicioDatos > periodoAnterior.desde) return null
  return calcularVariacion(actual, anterior)
}

/** Indica si el período llega hasta hoy, o sea si incluye el mes en curso. */
export const periodoEnCurso = (periodo: Periodo, hoy: Date): boolean => periodo.hasta >= aISO(hoy)
